import { AuditLogExplorerTypes } from '../types/auditLogExplorer.types';

/** Results table columns. `sortBy` (when set) maps to the contract sort enum. */
export interface AuditColumn {
    field: string;
    headerName: string;
    align?: 'left' | 'right';
    sortBy?: AuditLogExplorerTypes.SortBy;
}

export const AUDIT_COLUMNS: AuditColumn[] = [
    { field: 'timestamp', headerName: 'Timestamp (UTC)', sortBy: 'timestamp' },
    { field: 'method', headerName: 'Method', sortBy: 'method' },
    { field: 'status', headerName: 'Status', sortBy: 'statusCode' },
    { field: 'action', headerName: 'Action' },
    { field: 'endpoint', headerName: 'Endpoint', sortBy: 'endpoint' },
    { field: 'source', headerName: 'Source', sortBy: 'apiSource' },
    { field: 'subscription', headerName: 'Organization', sortBy: 'subscriptionId' },
    { field: 'user', headerName: 'User', sortBy: 'userId' },
    { field: 'response', headerName: 'Response', align: 'right', sortBy: 'responseTimeMs' },
    { field: 'error', headerName: '', align: 'left' },
];

// Time presets supported by the FROZEN contract (1h / 24h / 7d / custom).
export const TIME_PRESET_OPTIONS: { label: string; value: AuditLogExplorerTypes.TimePreset }[] = [
    { label: 'Last 1h', value: '1h' },
    { label: 'Last 24h', value: '24h' },
    { label: 'Last 7d', value: '7d' },
    { label: 'Custom…', value: 'custom' },
];

export const STATUS_OPTIONS: { label: string; value: AuditLogExplorerTypes.StatusClass | '' }[] = [
    { label: 'Any status', value: '' },
    { label: '2xx', value: '2xx' },
    { label: '3xx', value: '3xx' },
    { label: '4xx', value: '4xx' },
    { label: '5xx', value: '5xx' },
];

export const METHOD_OPTIONS: { label: string; value: AuditLogExplorerTypes.Method | '' }[] = [
    { label: 'Any method', value: '' },
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
    { label: 'PUT', value: 'PUT' },
    { label: 'PATCH', value: 'PATCH' },
    { label: 'DELETE', value: 'DELETE' },
];

export const API_SOURCE_OPTIONS: { label: string; value: string }[] = [
    { label: 'Any source', value: '' },
    { label: 'node-api', value: 'node-api' },
    { label: 'pom-2-api', value: 'pom-2-api' },
];

export const ROWS_PER_PAGE_OPTIONS = [50, 100, 500];

/**
 * Derive a concise human label from a real route pattern. Real endpoints are
 * versioned, multi-segment patterns (e.g. `/v1/alerts/:id`,
 * `/v1/subscriptions/:id/users`), so we drop the version prefix (`v1`, `v2`…)
 * and path params (`:id`), then humanize the remaining resource segments.
 *   /v1/alerts/:id            → "Alerts"
 *   /v1/subscriptions/:id/users → "Subscriptions Users"
 *   /v1/audit-log-explorer/search → "Audit Log Explorer Search"
 */
export const humanizeRoute = (path: string): string => {
    if (!path) return 'Request';
    const segments = String(path)
        .split('?')[0]
        .split('/')
        .filter(Boolean)
        .filter((s) => !/^v\d+$/i.test(s)) // drop version prefixes
        .filter((s) => !s.startsWith(':') && !s.startsWith('{')); // drop path params
    if (!segments.length) return 'Request';
    return segments
        .map((s) => s.replace(/[-_]/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2'))
        .join(' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const actionLabel = (endpoint: string): string => humanizeRoute(endpoint);

export const STATUS_TEXT: Record<number, string> = {
    200: 'OK', 201: 'Created', 204: 'No Content',
    301: 'Moved', 302: 'Found', 304: 'Not Modified',
    400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found',
    422: 'Unprocessable', 429: 'Too Many Requests',
    500: 'Server Error', 502: 'Bad Gateway', 503: 'Unavailable', 504: 'Gateway Timeout',
};

export const statusText = (code: number) => `${code} ${STATUS_TEXT[code] || ''}`.trim();
